import { forwardRef, useState, useEffect } from "react";
import { Row, Col } from "antd";
import { InputItem, DatePickerItem } from "../../../components/forms/FormItems";
import SwitchItem from "../../../components/forms/SwitchItem";
import AutoCompleteItem from "../../../components/forms/AutoCompleteItem";
import {
  BankBranchesSelect,
  MetaDataSelect,
} from "../../../components/forms/AutoCompleteFormItem";
import { FormattedMessage } from "react-intl";
import Surface from "../../../components/layouts/Surface";
import FormHeader from "../../../components/forms/FormHeader";
import Spacer from "../../../components/layouts/Spacer";
import ResponsiveItemsWrapper from "../../../components/layouts/ResponsiveItemsWrapper";
import { useSelector } from "react-redux";
import { useBanks } from "../../../hooks";
import moment from "moment";

const Payments = forwardRef((props, ref) => {
  const { smallSpace } = props;
  const banks = useBanks();

  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );
  const [show, setShow] = useState(false);
  const [paidByRelative, setPaidByRelative] = useState(false);
  const [masavEnabled, setMasavEnabled] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [bankId, setBankId] = useState(null);
  const changedValues = useSelector(
    (state) => state.patientDetails.changedItems.values
  );

  // Get current effective dates, prioritizing changed items over activePatient
  const effectiveFrom =
    changedValues["masav_charge.effective_from"] !== undefined
      ? changedValues["masav_charge.effective_from"]
      : activePatient?.masav_charge?.effective_from;

  const effectiveTo =
    changedValues["masav_charge.effective_to"] !== undefined
      ? changedValues["masav_charge.effective_to"]
      : activePatient?.masav_charge?.effective_to;

  // Disable dates in 'effective_from' picker that are after 'effective_to'
  const disabledEffectiveFromDate = (current) => {
    if (!effectiveTo) {
      return false; // Don't disable if 'effective_to' is not set
    }
    // Cannot select a date after 'effective_to'
    return current && current > moment(effectiveTo).endOf("day");
  };

  // Disable dates in 'effective_to' picker that are before or on the same day as 'effective_from'
  const disabledEffectiveToDate = (current) => {
    if (!effectiveFrom) {
      return false; // Don't disable if 'effective_from' is not set
    }
    // Cannot select a date before or on the same day as 'effective_from'
    return current && current < moment(effectiveFrom).startOf("day");
  };

  useEffect(() => {
    setBankId(
      activePatient.masav_charge?.bank_id
        ? activePatient.masav_charge?.bank_id
        : null
    );
  }, [banks, activePatient]);

  const onBankChange = (id) => {
    setBankId(id);
  };

  useEffect(() => {
    setShow(
      activePatient && activePatient.payments
        ? activePatient.payments.self_payment
        : false
    );
    setPaidByRelative(
      activePatient && activePatient.payments
        ? activePatient.payments.paid_by_relative
        : false
    );
    setMasavEnabled(
      activePatient && activePatient.masav_charge
        ? activePatient.masav_charge?.enabled
        : false
    );
    setIsDeactivated(
      activePatient && activePatient.masav_charge
        ? activePatient.masav_charge?.deactivated
        : false
    );
  }, [activePatient]);

  const onChange = (checked) => {
    setShow(checked);
  };

  const onMasavChange = (checked) => {
    setMasavEnabled(checked);
  };
  const onPaidByRelativeChange = (checked) => {
    setPaidByRelative(checked);
  };
  const onDeactivatedChange = (checked) => {
    setIsDeactivated(checked);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      <Surface smallSpace={smallSpace}>
        <FormHeader title={<FormattedMessage id="app.patients.payments" />} />
        <Row>
          <Col xs={24} sm={24} md={24} lg={21} xl={15} xxl={10}>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="payments.self_payment"
                  category="patient"
                  onChange={onChange}
                />
              </div>
            </ResponsiveItemsWrapper>

            {show && (
              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <SwitchItem
                    name="payments.paid_by_relative"
                    category="patient"
                    onChange={onPaidByRelativeChange}
                  />
                </div>
                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  <InputItem
                    name="payments.name"
                    category="patient"
                    required={paidByRelative}
                  />
                </div>
              </ResponsiveItemsWrapper>
            )}

            {show ? (
              <div>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="payments.address"
                      category="patient"
                      required={paidByRelative}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <AutoCompleteItem
                      name="payments.city"
                      category="patient"
                      city={true}
                      required={paidByRelative}
                    />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem name="payments.postal_code" category="patient" />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="payments.email"
                      category="patient"
                      email={true}
                    />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="payments.debit_account_number"
                      category="patient"
                      required={true}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}></div>
                </ResponsiveItemsWrapper>
              </div>
            ) : null}
          </Col>
        </Row>

        <Row>
          <Col xs={24} sm={24} md={24} lg={21} xl={15} xxl={10}>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="masav_charge.enabled"
                  category="patient"
                  onChange={onMasavChange}
                />
              </div>
            </ResponsiveItemsWrapper>

            {masavEnabled && (
              <div>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <MetaDataSelect
                      name="masav_charge.bank_id"
                      category="patient"
                      metadataName={"banks"}
                      onChange={onBankChange}
                      required={true}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    {bankId ? (
                      <BankBranchesSelect
                        name="masav_charge.bank_branch_id"
                        category="patient"
                        extraParams={{ id: bankId }}
                        disabled={bankId === undefined}
                        required={true}
                      />
                    ) : null}
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="masav_charge.account_number"
                      category="patient"
                      onlyNumbers={true}
                      ninePositiveNumbers={true}
                      required={true}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="masav_charge.charge_day"
                      category="patient"
                      number={true}
                      dayInMonth={true}
                      required={true}
                    />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <DatePickerItem
                      name="masav_charge.effective_from"
                      category="patient"
                      disabledDate={disabledEffectiveFromDate}
                      required={true}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <DatePickerItem
                      name="masav_charge.effective_to"
                      category="patient"
                      disabledDate={disabledEffectiveToDate}
                      required={true}
                    />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <SwitchItem
                      name="masav_charge.deactivated"
                      category="patient"
                      onChange={onDeactivatedChange}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  {isDeactivated && (
                    <div style={{ flex: 1 }}>
                      <InputItem
                        name="masav_charge.deactivated_reason"
                        category="patient"
                      />
                    </div>
                  )}
                  {!isDeactivated && <div style={{ flex: 1 }}></div>}
                </ResponsiveItemsWrapper>
              </div>
            )}
          </Col>
        </Row>
      </Surface>
    </div>
  );
});

export default Payments;
